import { FunctionComponent } from 'react'
import { useMount } from 'react-use'
import styled from 'styled-components'
import type { OurIntlShape } from '@/i18n/typing'
import PlatformImage from '@/utils/export-pptx/components/platform-image'
import {
  KolAvatar,
  SlideContainer,
  SlideTitle,
} from '@/utils/export-pptx/components/slides/styles'
import { PptxObjectType } from '@/utils/export-pptx/constants'
import type { KolPerformanceStatsSlide } from '@/utils/export-pptx/types/slide'
import { getThumbnail } from '@/utils/get-thumbnails'

interface SlideProps {
  slide: KolPerformanceStatsSlide
  onSlideReady?: (slide: KolPerformanceStatsSlide) => void
  formatMessage: OurIntlShape['formatMessage']
}

const SlideComponent: FunctionComponent<SlideProps> = ({
  slide,
  onSlideReady,
  formatMessage,
}) => {
  useMount(() => {
    onSlideReady?.(slide)
  })

  return (
    <SlideContainer data-pptx-object={PptxObjectType.Shape}>
      <SlideTitle data-pptx-object={PptxObjectType.Shape} mb={2}>
        <span data-pptx-object={PptxObjectType.Text}>
          {formatMessage({
            id: 'insight_report:slide_kol_performance_stats_title',
          })}
        </span>
      </SlideTitle>

      <SlideContent>
        <CardContentHeader>
          <ColumnItemXLarge data-pptx-object={PptxObjectType.Text}>
            {formatMessage({ id: 'general:column_title_kol_name' })}
          </ColumnItemXLarge>

          <ColumnItemLarge data-pptx-object={PptxObjectType.Text}>
            {formatMessage({ id: 'general:column_title_follower_count' })}
          </ColumnItemLarge>

          <ColumnItemMedium data-pptx-object={PptxObjectType.Text}>
            {formatMessage({ id: 'general:field_name_cooperation_content' })}
          </ColumnItemMedium>

          {slide.payload.includes.publishedAt && (
            <ColumnItemMedium data-pptx-object={PptxObjectType.Text}>
              {formatMessage({
                id: 'insight_report:slide_kol_performance_stats_published_at',
              })}
            </ColumnItemMedium>
          )}

          {slide.payload.includes.likes && (
            <ColumnItemSmall data-pptx-object={PptxObjectType.Text}>
              {formatMessage({ id: 'general:column_title_like_count' })}
            </ColumnItemSmall>
          )}

          {slide.payload.includes.comments && (
            <ColumnItemSmall data-pptx-object={PptxObjectType.Text}>
              {formatMessage({ id: 'general:column_title_comment_count' })}
            </ColumnItemSmall>
          )}

          {slide.payload.includes.shares && (
            <ColumnItemSmall data-pptx-object={PptxObjectType.Text}>
              {formatMessage({ id: 'general:column_title_share_count' })}
            </ColumnItemSmall>
          )}

          {slide.payload.includes.engagement && (
            <ColumnItemSmall data-pptx-object={PptxObjectType.Text}>
              {formatMessage({ id: 'general:column_title_engagement_count' })}
            </ColumnItemSmall>
          )}

          {slide.payload.includes.engagementRate && (
            <ColumnItemSmall data-pptx-object={PptxObjectType.Text}>
              {formatMessage({ id: 'general:column_title_engagement_rate' })}
            </ColumnItemSmall>
          )}

          {slide.payload.includes.view && (
            <ColumnItemSmall data-pptx-object={PptxObjectType.Text}>
              {formatMessage({ id: 'general:column_title_watch_count' })}
            </ColumnItemSmall>
          )}

          {slide.payload.includes.viewRate && (
            <ColumnItemSmall data-pptx-object={PptxObjectType.Text}>
              {formatMessage({ id: 'general:column_title_view_rate' })}
            </ColumnItemSmall>
          )}

          {slide.payload.includes.reach && (
            <ColumnItemSmall data-pptx-object={PptxObjectType.Text}>
              {formatMessage({ id: 'general:column_title_touch_count' })}
            </ColumnItemSmall>
          )}

          {slide.payload.includes.reachRate && (
            <ColumnItemSmall data-pptx-object={PptxObjectType.Text}>
              {formatMessage({ id: 'general:column_title_touch_rate' })}
            </ColumnItemSmall>
          )}

          {slide.payload.includes.click && (
            <ColumnItemMedium data-pptx-object={PptxObjectType.Text}>
              {formatMessage({ id: 'general:column_title_link_click_count' })}
            </ColumnItemMedium>
          )}

          {slide.payload.includes.clickRate && (
            <ColumnItemMedium data-pptx-object={PptxObjectType.Text}>
              {formatMessage({ id: 'general:column_title_link_click_rate' })}
            </ColumnItemMedium>
          )}
        </CardContentHeader>

        {slide.payload.kolList.map((item) => (
          <CardContentItem
            data-pptx-object={PptxObjectType.Shape}
            key={item.uuid}
          >
            <ColumnItemXLarge>
              <KolAvatar
                alt={`kol-avatar-${item.uuid}`}
                data-pptx-object={PptxObjectType.Image}
                src={getThumbnail(item.uuid)}
              />

              <KolName data-pptx-object={PptxObjectType.Text}>
                {item.name}
              </KolName>
            </ColumnItemXLarge>

            <PlatformList>
              {item.platformList.map((p) => (
                <PlatformListItem key={p.platform}>
                  <ColumnItemLarge>
                    <PlatformImage
                      height={16}
                      platform={p.platform}
                      width={16}
                    />

                    <span data-pptx-object={PptxObjectType.Text}>
                      {p.followers}
                    </span>
                  </ColumnItemLarge>

                  <div>
                    {p.contents.map((c) => (
                      <PlatformListItemContentItem key={c.name}>
                        <ColumnItemMedium
                          data-pptx-object={PptxObjectType.Text}
                        >
                          {c.name}
                        </ColumnItemMedium>

                        {slide.payload.includes.publishedAt && (
                          <ColumnItemMedium
                            data-pptx-object={PptxObjectType.Text}
                          >
                            {c.date}
                          </ColumnItemMedium>
                        )}

                        {slide.payload.includes.likes && (
                          <ColumnItemSmall
                            data-pptx-object={PptxObjectType.Text}
                          >
                            {c.likes}
                          </ColumnItemSmall>
                        )}

                        {slide.payload.includes.comments && (
                          <ColumnItemSmall
                            data-pptx-object={PptxObjectType.Text}
                          >
                            {c.comments}
                          </ColumnItemSmall>
                        )}

                        {slide.payload.includes.shares && (
                          <ColumnItemSmall
                            data-pptx-object={PptxObjectType.Text}
                          >
                            {c.shares}
                          </ColumnItemSmall>
                        )}

                        {slide.payload.includes.engagement && (
                          <ColumnItemSmall
                            data-pptx-object={PptxObjectType.Text}
                          >
                            {c.engagement}
                          </ColumnItemSmall>
                        )}

                        {slide.payload.includes.engagementRate && (
                          <ColumnItemSmall
                            data-pptx-object={PptxObjectType.Text}
                          >
                            {c.engagementRate}
                          </ColumnItemSmall>
                        )}

                        {slide.payload.includes.view && (
                          <ColumnItemSmall
                            data-pptx-object={PptxObjectType.Text}
                          >
                            {c.view}
                          </ColumnItemSmall>
                        )}

                        {slide.payload.includes.viewRate && (
                          <ColumnItemSmall
                            data-pptx-object={PptxObjectType.Text}
                          >
                            {c.viewRate}
                          </ColumnItemSmall>
                        )}

                        {slide.payload.includes.reach && (
                          <ColumnItemSmall
                            data-pptx-object={PptxObjectType.Text}
                          >
                            {c.reach}
                          </ColumnItemSmall>
                        )}

                        {slide.payload.includes.reachRate && (
                          <ColumnItemSmall
                            data-pptx-object={PptxObjectType.Text}
                          >
                            {c.reachRate}
                          </ColumnItemSmall>
                        )}

                        {slide.payload.includes.click && (
                          <ColumnItemMedium
                            data-pptx-object={PptxObjectType.Text}
                          >
                            {c.click}
                          </ColumnItemMedium>
                        )}

                        {slide.payload.includes.clickRate && (
                          <ColumnItemMedium
                            data-pptx-object={PptxObjectType.Text}
                          >
                            {c.clickRate}
                          </ColumnItemMedium>
                        )}
                      </PlatformListItemContentItem>
                    ))}
                  </div>
                </PlatformListItem>
              ))}
            </PlatformList>
          </CardContentItem>
        ))}

        {slide.payload.total && (
          <CardContentFooter>
            <ColumnItemXLarge data-pptx-object={PptxObjectType.Text}>
              {formatMessage({ id: 'general:total_text' })}
            </ColumnItemXLarge>
            <ColumnItemLarge />
            <ColumnItemMedium />

            {slide.payload.includes.publishedAt && <ColumnItemMedium />}

            {slide.payload.includes.likes && (
              <ColumnItemSmall data-pptx-object={PptxObjectType.Text}>
                {slide.payload.total.likes}
              </ColumnItemSmall>
            )}

            {slide.payload.includes.comments && (
              <ColumnItemSmall data-pptx-object={PptxObjectType.Text}>
                {slide.payload.total.comments}
              </ColumnItemSmall>
            )}

            {slide.payload.includes.shares && (
              <ColumnItemSmall data-pptx-object={PptxObjectType.Text}>
                {slide.payload.total.shares}
              </ColumnItemSmall>
            )}

            {slide.payload.includes.engagement && (
              <ColumnItemSmall data-pptx-object={PptxObjectType.Text}>
                {slide.payload.total.engagement}
              </ColumnItemSmall>
            )}

            {slide.payload.includes.engagementRate && (
              <ColumnItemSmall data-pptx-object={PptxObjectType.Text}>
                {slide.payload.total.engagementRate}
              </ColumnItemSmall>
            )}

            {slide.payload.includes.view && (
              <ColumnItemSmall data-pptx-object={PptxObjectType.Text}>
                {slide.payload.total.view}
              </ColumnItemSmall>
            )}

            {slide.payload.includes.viewRate && (
              <ColumnItemSmall data-pptx-object={PptxObjectType.Text}>
                {slide.payload.total.viewRate}
              </ColumnItemSmall>
            )}

            {slide.payload.includes.reach && (
              <ColumnItemSmall data-pptx-object={PptxObjectType.Text}>
                {slide.payload.total.reach}
              </ColumnItemSmall>
            )}

            {slide.payload.includes.reachRate && (
              <ColumnItemSmall data-pptx-object={PptxObjectType.Text}>
                {slide.payload.total.reachRate}
              </ColumnItemSmall>
            )}

            {slide.payload.includes.click && (
              <ColumnItemMedium data-pptx-object={PptxObjectType.Text}>
                {slide.payload.total.click}
              </ColumnItemMedium>
            )}

            {slide.payload.includes.clickRate && (
              <ColumnItemMedium data-pptx-object={PptxObjectType.Text}>
                {slide.payload.total.clickRate}
              </ColumnItemMedium>
            )}
          </CardContentFooter>
        )}
      </SlideContent>
    </SlideContainer>
  )
}

const SlideContent = styled.div`
  height: 600px;

  font-size: 14px;
  font-weight: 400;

  color: ${({ theme }): string => theme.colors.text.primary};
`

const CardContentHeader = styled.div`
  height: 56px;

  font-size: 10px;
  font-weight: 600;

  display: flex;
  align-items: center;
  justify-content: flex-start;
`

const CardContentFooter = styled(CardContentHeader)`
  font-size: 12px;
`

const CardContentItem = styled.div`
  min-height: 56px;

  flex: 1;

  display: flex;
  align-items: center;
  justify-content: flex-start;

  font-size: 12px;
  background-color: ${({ theme }): string => theme.colors.background.grey};

  &:nth-child(even) {
    background-color: ${({ theme }): string => theme.colors.background.white};
  }
`

const KolName = styled.span`
  margin-left: 8px;
`

const PlatformList = styled.div``

const PlatformListItem = styled.div`
  min-height: 56px;

  flex: 1;

  display: flex;
  align-items: center;
  justify-content: flex-start;
`

const PlatformListItemContentItem = styled.div`
  height: 56px;

  display: flex;
  align-items: center;
  justify-content: flex-start;
`

const ColumnItemLarge = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;

  flex-shrink: 0;
  width: 112px;
`

const ColumnItemMedium = styled.div`
  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 96px;

  white-space: nowrap;
`

const ColumnItemSmall = styled.div`
  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: flex-start;

  width: 76px;
  white-space: nowrap;
`

const ColumnItemXLarge = styled.div`
  width: 140px;
  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: flex-start;
`

export default SlideComponent
